import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { getRoleId } from "Common/Components/getRoleId";
import BreadCrumb from "Common/BreadCrumb";

const AdminSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // مقادیر تنظیمات
    const [minDischargeToInspection, setMinDischargeToInspection] = useState<number | string>("");
    const [driverDeadline, setDriverDeadline] = useState<number | string>("");

    const userRole = getRoleId(localStorage.getItem("app_havir")) || 0;
    const isAdmin = userRole === 0 || userRole === 1; // ادمین یا سوپرادمین

    // دریافت هر دو تنظیمات
    const fetchSettings = async () => {
        if (!isAdmin) return;
        setLoading(true);
        try {
            const token = localStorage.getItem("app_havir");
            const headers = { Authorization: `Bearer ${token}` };

            const [resMin, resDeadline] = await Promise.all([
                axios.get(
                    `https://rasad-api.havirard.ir/settings/min-discharge-to-inspection-hours`,
                    { headers }
                ),
                axios.get(
                    `https://rasad-api.havirard.ir/settings/driver-discharge-submission-deadline-hours`,
                    { headers }
                ),
            ]);

            setMinDischargeToInspection(
                resMin.data.minimum_discharge_to_inspection_hours ?? 24
            );
            setDriverDeadline(
                resDeadline.data.driver_discharge_submission_deadline_hours ?? ""
            );
        } catch (err: any) {
            toast.error("خطا در دریافت تنظیمات");
        } finally {
            setLoading(false);
        }
    };

    // ذخیره‌ی هر دو تنظیمات
    const handleSave = async () => {
        // اعتبارسنجی
        const minVal = Number(minDischargeToInspection);
        if (isNaN(minVal) || minVal < 0) {
            toast.error("حداقل فاصله تخلیه تا بازرسی باید عددی بزرگتر یا مساوی صفر باشد");
            return;
        }

        let deadlineVal = null;
        if (driverDeadline !== "" && driverDeadline !== null && driverDeadline !== undefined) {
            const num = Number(driverDeadline);
            if (isNaN(num) || num < 0) {
                toast.error("مهلت تخلیه راننده باید عددی بزرگتر یا مساوی صفر باشد (یا خالی گذاشته شود)");
                return;
            }
            deadlineVal = num;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("app_havir");
            const headers = { Authorization: `Bearer ${token}` };

            await Promise.all([
                axios.put(
                    `https://rasad-api.havirard.ir/settings/min-discharge-to-inspection-hours/`,
                    { minimum_discharge_to_inspection_hours: minVal },
                    { headers }
                ),
                axios.put(
                    `https://rasad-api.havirard.ir/settings/driver-discharge-submission-deadline-hours/`,
                    { driver_discharge_submission_deadline_hours: deadlineVal },
                    { headers }
                ),
            ]);

            toast.success("تنظیمات با موفقیت ذخیره شد");
        } catch (err: any) {
            toast.error(err.response?.data?.message || "خطا در ذخیره تنظیمات");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        // پاک‌سازی overflow (مشابه سایر صفحات)
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // اگر ادمین نیست
    if (!isAdmin) {
        return (
            <React.Fragment>
                <BreadCrumb pageTitle="تنظیمات" title="مدیریت" />
                <div className="card">
                    <div className="card-body text-center text-red-500 py-10">
                        شما دسترسی به این بخش ندارید.
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="تنظیمات سیستم" title="مدیریت" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

            <div className="card">
                <div className="card-body">
                    <h5 className="text-lg font-bold mb-6">تنظیمات مربوط به تخلیه و بازرسی</h5>

                    {loading ? (
                        <p className="text-gray-500">در حال بارگذاری...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                            {/* فیلد اول: حداقل فاصله تخلیه تا بازرسی */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    حداقل فاصله تخلیه تا بازرسی
                                    <span className="text-xs text-gray-500 mr-2">(ساعت)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={minDischargeToInspection}
                                    onChange={(e) => setMinDischargeToInspection(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                                    placeholder="مثال: ۲۴"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    مقدار پیش‌فرض: ۲۴ ساعت
                                </p>
                            </div>

                            {/* فیلد دوم: مهلت تخلیه راننده */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    مهلت تخلیه راننده
                                    <span className="text-xs text-gray-500 mr-2">(ساعت - خالی = بدون محدودیت)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={driverDeadline}
                                    onChange={(e) => setDriverDeadline(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                                    placeholder="مثال: ۴۸ یا خالی"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    اگر خالی بماند، محدودیت اعمال نمی‌شود.
                                </p>
                            </div>

                            <div className="md:col-span-2 mt-4">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-6 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all"
                                >
                                    {saving ? "در حال ذخیره..." : "ذخیره تنظیمات"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default AdminSettings;